import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { confirmDelete } from '../utils/confirmDelete';
import ModalPortal from '../components/ModalPortal';
import {
  Bot,
  Plus,
  Settings,
  Play,
  Pause,
  Brain,
  Zap,
  ArrowLeft,
  Trash2,
  Loader2
} from 'lucide-react';

// Components
import ChatbotFlowBuilder from '../components/ChatbotFlowBuilder';
import ChatbotAITrainer from '../components/ChatbotAITrainer';
import { CardSkeleton } from '../components/Skeleton';

const Chatbot = () => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Fetch current chatbots
  const { data: chatbotsData, isLoading } = useQuery(
    ['chatbots', currentWorkspace?.id],
    () => apiClient.get('/api/chatbot').then(res => res.data),
    {
      enabled: !!currentWorkspace?.id,
      onError: (err) => {
        if (err.response?.status !== 404) {
          toast.error('Failed to load chatbots');
        }
      }
    }
  );

  const chatbots = useMemo(() => chatbotsData?.chatbots || [], [chatbotsData?.chatbots]);
  const hasFlowChatbot = chatbots.some(cb => cb.chatbot_type === 'flow');
  const hasAIChatbot = chatbots.some(cb => cb.chatbot_type === 'ai');

  // Don't auto-select chatbot - let user choose when to edit

  // Fetch languages
  const { data: languages } = useQuery(
    'languages',
    () => apiClient.get('/api/chatbot/languages').then(res => res.data.languages),
    { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
  );

  // Create chatbot mutation
  const createMutation = useMutation(
    (data) => apiClient.post('/api/chatbot', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chatbots', currentWorkspace?.id]);
        setShowCreateModal(false);
        toast.success('Chatbot created successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create chatbot');
      }
    }
  );

  // Update chatbot mutation
  const updateMutation = useMutation(
    ({ id, data }) => apiClient.put(`/api/chatbot/${id}`, data),
    {
      onSuccess: (_, { id, data }) => {
        queryClient.invalidateQueries(['chatbots', currentWorkspace?.id]);
        // Sync selected chatbot with the update so UI shows new status immediately (no cache delay)
        setSelectedChatbot((prev) =>
          prev && prev.id === id ? { ...prev, ...data } : prev
        );
        toast.success('Chatbot updated successfully');
      },
      onError: (error, { id, data }) => {
        // Revert optimistic status if update failed
        setSelectedChatbot((prev) =>
          prev && prev.id === id
            ? { ...prev, status: data.status === 'active' ? 'paused' : 'active' }
            : prev
        );
        toast.error(error.response?.data?.error || 'Failed to update chatbot');
      }
    }
  );

  // Delete chatbot mutation
  const deleteMutation = useMutation(
    (id) => apiClient.delete(`/api/chatbot/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chatbots', currentWorkspace?.id]);
        setSelectedChatbot(null);
        toast.success('Chatbot deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete chatbot');
      }
    }
  );


  const handleCreateChatbot = (chatbotType) => {
    // Check if this type already exists
    if ((chatbotType === 'flow' && hasFlowChatbot) || (chatbotType === 'ai' && hasAIChatbot)) {
      toast.error(`${chatbotType === 'flow' ? 'Flow-based' : 'AI-powered'} chatbot already exists`);
      return;
    }

    const chatbotData = {
      name: chatbotType === 'flow' ? 'Flow-Based Chatbot' : 'AI-Powered Chatbot',
      description: chatbotType === 'flow'
        ? 'Automated customer support with visual flow builder'
        : 'Intelligent chatbot with natural language understanding',
      chatbot_type: chatbotType,
      status: 'draft',
      trigger_config: {
        trigger_on: 'all_messages'
      },
      default_language: 'en'
    };
    createMutation.mutate(chatbotData);
  };

  const handleUpdateChatbot = (id, data) => {
    updateMutation.mutate({ id, data });
  };

  const toggleChatbotStatus = () => {
    const newStatus = selectedChatbot.status === 'active' ? 'paused' : 'active';
    // Optimistic update: show new status immediately so Activate/Deactivate reflects without waiting for cache
    setSelectedChatbot((prev) => (prev ? { ...prev, status: newStatus } : null));
    handleUpdateChatbot(selectedChatbot.id, { status: newStatus });
  };

  const handleDeleteChatbot = () => {
    if (confirmDelete(`Are you sure you want to delete "${selectedChatbot.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(selectedChatbot.id);
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Bot className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Workspace Selected</h3>
          <p className="mt-1 text-sm text-gray-500">Please select a workspace to manage chatbots.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Bot className="mr-3 h-8 w-8 text-blue-600" />
                🤖 Chatbot Management
              </h1>
              <p className="mt-2 text-gray-600">
                Configure Manual Flow and AI/MLM chatbots for automated customer conversations
              </p>
            </div>
            {chatbots.length > 0 && (!hasFlowChatbot || !hasAIChatbot) && (
              <button
                onClick={() => setShowCreateModal(true)}
                disabled={createMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isLoading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-5 w-5" />
                )}
                {createMutation.isLoading ? 'Creating...' : `Add ${hasFlowChatbot ? 'AI' : 'Flow'} Chatbot`}
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        {isLoading ? (
          <div className="space-y-6">
            <CardSkeleton count={2} />
          </div>
        ) : selectedChatbot ? (
          <div className="space-y-6">
            {/* Back to Overview Button */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSelectedChatbot(null)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Overview
              </button>
            </div>

            {/* Chatbot Header Card */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-xl ${selectedChatbot.chatbot_type === 'ai' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                    {selectedChatbot.chatbot_type === 'ai' ? (
                      <Brain className="h-8 w-8 text-purple-600" />
                    ) : (
                      <Zap className="h-8 w-8 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedChatbot.name}</h3>
                    <p className="text-gray-600 mt-1">{selectedChatbot.description}</p>
                    <div className="flex items-center mt-3 space-x-3">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedChatbot.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : selectedChatbot.status === 'paused'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                        }`}>
                        {selectedChatbot.status === 'active' ? '🟢 Active' : selectedChatbot.status === 'paused' ? '🟡 Paused' : '⚪ Draft'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {selectedChatbot.chatbot_type === 'ai' ? '🤖 AI/MLM Chatbot' : '🔀 Manual Flow Chatbot'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Quick Actions</div>
                    <div className="flex items-center space-x-2 mt-2">
                      <button
                        onClick={toggleChatbotStatus}
                        disabled={updateMutation.isLoading}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${selectedChatbot.status === 'active'
                          ? 'text-white bg-red-600 hover:bg-red-700'
                          : 'text-white bg-green-600 hover:bg-green-700'
                          }`}
                      >
                        {updateMutation.isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {selectedChatbot.status === 'active' ? 'Deactivating...' : 'Activating...'}
                          </>
                        ) : selectedChatbot.status === 'active' ? (
                          <>
                            <Pause className="mr-2 h-4 w-4" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Play className="mr-2 h-4 w-4" />
                            Activate
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => navigate(`/chatbot/${selectedChatbot.id}/training`)}
                        disabled={updateMutation.isLoading}
                        className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-lg text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Brain className="mr-2 h-4 w-4" />
                        Training
                      </button>
                      <button
                        onClick={() => setShowEditModal(true)}
                        disabled={updateMutation.isLoading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updateMutation.isLoading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Settings className="mr-2 h-4 w-4" />
                        )}
                        Settings
                      </button>
                      <button
                        onClick={handleDeleteChatbot}
                        disabled={deleteMutation.isLoading}
                        className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Builder Content - No Tabs */}
            <div className="bg-white shadow rounded-lg">
              {selectedChatbot.chatbot_type === 'flow' ? (
                // Flow-Based: Show Flow Builder
                <div className="p-6">
                  <ChatbotFlowBuilder
                    chatbot={selectedChatbot}
                    onSave={(data) => handleUpdateChatbot(selectedChatbot.id, data)}
                  />
                </div>
              ) : (
                // AI-Based: Show AI Trainer
                <div className="p-6">
                  <ChatbotAITrainer
                    chatbot={selectedChatbot}
                    languages={languages || []}
                    onSave={(data) => handleUpdateChatbot(selectedChatbot.id, data)}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Chatbots Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Manual Flow Chatbot Status */}
              <div className={`bg-white border rounded-lg p-6 ${hasFlowChatbot ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${hasFlowChatbot ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Zap className={`h-6 w-6 ${hasFlowChatbot ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Manual Flow</h3>
                      <p className="text-sm text-gray-600">Rule-based conversations</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {hasFlowChatbot ? (
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Configured
                        </span>
                        {chatbots.find(cb => cb.chatbot_type === 'flow')?.status === 'active' && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Active
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Not Set
                      </span>
                    )}
                  </div>
                </div>

                {hasFlowChatbot && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {chatbots.find(cb => cb.chatbot_type === 'flow')?.name}
                    </div>
                    <button
                      onClick={() => setSelectedChatbot(chatbots.find(cb => cb.chatbot_type === 'flow'))}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Manage →
                    </button>
                  </div>
                )}
              </div>

              {/* AI/MLM Chatbot Status */}
              <div className={`bg-white border rounded-lg p-6 ${hasAIChatbot ? 'border-purple-200 bg-purple-50' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${hasAIChatbot ? 'bg-purple-100' : 'bg-gray-100'}`}>
                      <Brain className={`h-6 w-6 ${hasAIChatbot ? 'text-purple-600' : 'text-gray-400'}`} />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">AI/MLM</h3>
                      <p className="text-sm text-gray-600">Intelligent conversations</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {hasAIChatbot ? (
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Configured
                        </span>
                        {chatbots.find(cb => cb.chatbot_type === 'ai')?.status === 'active' && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Active
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Not Set
                      </span>
                    )}
                  </div>
                </div>

                {hasAIChatbot && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {chatbots.find(cb => cb.chatbot_type === 'ai')?.name}
                    </div>
                    <button
                      onClick={() => setSelectedChatbot(chatbots.find(cb => cb.chatbot_type === 'ai'))}
                      className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                    >
                      Manage →
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Add Chatbot Button Section */}
            {chatbots.length === 0 && (
              <div className="bg-white shadow rounded-lg p-8 text-center">
                <Bot className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No chatbots configured</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating your first chatbot.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    disabled={createMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createMutation.isLoading ? (
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-5 w-5" />
                    )}
                    {createMutation.isLoading ? 'Creating...' : 'Create Chatbot'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Chatbot Modal */}
      {showCreateModal && (
        <ModalPortal>
        <div className="fixed inset-0 bg-gray-600 mt-0 bg-opacity-50 z-[100] flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-medium text-gray-900">Add Another Chatbot</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                <span className="sr-only">Close</span>
                ×
              </button>
            </div>

            <div className="px-6 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Flow-Based Option */}
                <div
                  className={`border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:bg-blue-50 transition-colors ${createMutation.isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => !createMutation.isLoading && handleCreateChatbot('flow')}
                >
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Zap className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-medium text-gray-900">Flow-Based Chatbot</h4>
                      <p className="text-sm text-gray-600">Rule-based conversations</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Create structured conversation flows with visual builder and keyword matching.
                  </p>
                  <button
                    disabled={createMutation.isLoading}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createMutation.isLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    {createMutation.isLoading ? 'Creating...' : 'Add Flow Chatbot'}
                  </button>
                </div>

                {/* AI-Powered Option */}
                <div
                  className={`border border-gray-200 rounded-lg p-6 hover:border-purple-300 hover:bg-purple-50 transition-colors ${createMutation.isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => !createMutation.isLoading && handleCreateChatbot('ai')}
                >
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Brain className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-medium text-gray-900">AI-Powered Chatbot</h4>
                      <p className="text-sm text-gray-600">Intelligent conversations</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Build intelligent chatbots with natural language understanding and learning.
                  </p>
                  <button
                    disabled={createMutation.isLoading}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createMutation.isLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    {createMutation.isLoading ? 'Creating...' : 'Add AI Chatbot'}
                  </button>
                </div>
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Edit Chatbot Modal */}
      {showEditModal && selectedChatbot && (
        <EditChatbotModal
          chatbot={selectedChatbot}
          onClose={() => setShowEditModal(false)}
          onUpdate={(data) => handleUpdateChatbot(selectedChatbot.id, data)}
          languages={languages || []}
          isLoading={updateMutation.isLoading}
        />
      )}
    </div>
  );
};

// Edit Chatbot Modal Component
const EditChatbotModal = ({ chatbot, onClose, onUpdate, languages, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: chatbot.name || '',
    description: chatbot.description || '',
    status: chatbot.status || 'draft',
    trigger_config: chatbot.trigger_config || { trigger_on: 'all_messages' },
    default_language: chatbot.default_language || 'en'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
    onClose();
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 bg-gray-600 mt-0 bg-opacity-50 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-medium text-gray-900">Edit Chatbot</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            <span className="sr-only">Close</span>
            ×
          </button>
        </div>

        <div className="px-6 pb-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chatbot Name
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter chatbot name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this chatbot does"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Language
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={formData.default_language}
                    onChange={(e) => setFormData({ ...formData, default_language: e.target.value })}
                  >
                    {languages && languages.length > 0 ? (
                      languages.map(lang => (
                        <option key={lang.code} value={lang.code}>
                          {lang.nativeName} ({lang.name})
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="en">English (English)</option>
                        <option value="es">Español (Spanish)</option>
                        <option value="fr">Français (French)</option>
                        <option value="de">Deutsch (German)</option>
                        <option value="hi">हिन्दी (Hindi)</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Chatbot'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default Chatbot;